import { CommonModule } from '@angular/common';
import { Component, OnInit, signal, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { OauthService } from '../../services/oauth.service';
import { RepoService } from '../../services/repo.service';
import { ToastService } from '../../services/toast.service';
import { WorkflowType } from '../../services/workflow.service';
import { TextareaComponent } from '../../components/textarea/textarea.component';
import { BreakpointObserver } from '@angular/cdk/layout';
import { map } from 'rxjs';
import { RedeemCodeComponent } from '../../components/dialogue/redeem-code/redeem-code.component';
// import { components } from '../../schema/backend';

// import { WorkflowRegistryService } from '../../workflows/services/workflow-registry.service';

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [CommonModule, FormsModule, TextareaComponent, RedeemCodeComponent],
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.css'],
})
export class HomeComponent implements OnInit {
  @ViewChild(TextareaComponent) textareaComponent!: TextareaComponent;

  createRepoVisible: boolean = false;
  selectedWorkflow = signal<WorkflowType | null>(null);
  isMobileView = signal<boolean>(false);

  constructor(
    private route: Router,
    private auth: OauthService,
    private toastService: ToastService,
    private repoService: RepoService,
    private bpObs: BreakpointObserver
  ) {
    this.bpObs.observe([
      '(max-width: 1000px)'
    ])
      .pipe(
        map(state => state.matches),
      ).subscribe(isMobile => {
        this.isMobileView.set(isMobile);
      });
  }

  samplePrompts = [
    "What is the distribution of age, sex, and race in the study population?",
    "Are there any implausible demographic values?",
    "What is the proportion of subjects by country and site?",
    "Is baseline weight or BMI missing for any subjects?",
    "Do subjects randomized differ significantly in baseline characteristics?",
    "What are the most frequently reported adverse events?",
    "How many subjects experienced serious adverse events?",
    "What is the distribution of adverse event severity across treatment arms?",
    "Are there any AEs reported after the subject's end of treatment?",
    "Are start and end dates of AEs missing or inconsistent?",
    "Which lab parameters are most frequently measured?",
    "How often do abnormal results occur, and in which parameters?",
    "Are lab units consistent for each parameter across all subjects and visits?",
    "Are there any lab values that fall outside physiologically plausible ranges?",
    "Do any subjects show a worsening trend in a key lab parameter over time?",
    "What is the time from screening to first dose for each subject?",
    "How long does treatment last for each subject?",
    "Do any subjects have treatment or AE dates before randomization or informed consent?",
    "Are there subjects who discontinued early? What are the reasons?",
    "Are there any discrepancies between treatment arms assigned in DM and those recorded in EX or DS domains?",
    "Do all subjects in AE, EX, LB, and VS domains appear in the DM domain?",
    "Are there any subjects with data in AE or LB but no treatment record in EX?",
    "Are the visit dates aligned across domains for the same subject and visit?",
    "Are there any subjects whose AE onset precedes study start or follows end?",
    "Do the reported treatment arms in DM match exposure data in EX?"
  ];

  displayedPrompts: string[] = [];
  isTypingPrompt: boolean = false;

  ngOnInit() {
    // this.router.queryParams.subscribe((queryParams) => {
    //   queryParams['access'] === 'pending' ? this.accessPending.set(true) : this.accessPending.set(false);
    // })
    // this.accessPending.set(this.router.parseUrl(this.router.url).queryParams["access"] === 'pending');
    this.displayedPrompts = this.getRandomPrompts(this.samplePrompts, 4);
  }

  getRandomPrompts(prompts: string[], count: number): string[] {
    const shuffled = prompts.sort(() => 0.5 - Math.random());
    return shuffled.slice(0, count);
  }

  async usePrompt(prompt: string): Promise<void> {
    if (this.textareaComponent.isTypingPrompt) return;
    this.createRepoVisible = false;

    this.textareaComponent.isTypingPrompt = true;
    this.textareaComponent.userInput = '';

    try {
      // Increase typing speed for faster typing
      const fastTypingSpeed = 5; // reduced from 10ms to 5ms per character

      for (let i = 0; i < prompt.length; i++) {
        this.textareaComponent.userInput += prompt[i];
        // this.userInput += prompt[i];
        await new Promise(resolve => setTimeout(resolve, fastTypingSpeed));
      }
      await new Promise(resolve => setTimeout(resolve, 100));

      // Temporarily set isTypingPrompt to false to allow handleSend to work
      const wasTyping = this.textareaComponent.isTypingPrompt;
      this.textareaComponent.isTypingPrompt = false;
      this.handleSend(prompt);
      this.textareaComponent.isTypingPrompt = wasTyping; // Restore the flag
    } catch (error) {
      console.error('Error typing prompt:', error);
      this.textareaComponent.isTypingPrompt = false;
    }
  }

  async handleSend(msgInput: string): Promise<void> {
    if (!msgInput.trim() || this.textareaComponent.isTypingPrompt) return;

    try {
      // Check if user is signed in
      const userInfo = await this.auth.getUser();
      if (!userInfo) {
        this.toastService.showError('Please sign in to continue');
        console.error('User not signed in');
        this.textareaComponent.isTypingPrompt = false;
        return;
      }

      // Check if a repository is selected
      if (!this.repoService.repo()) {
        this.repoSelectionError();
        return;
      }

      // Store only the user input in localStorage
      localStorage.setItem('pendingMessage', JSON.stringify({
        content: msgInput.trim()
      }));
      localStorage.setItem('selectedWorkflow', JSON.stringify(this.selectedWorkflow()));

      // Navigate to workspace
      this.route.navigate(['/workspace']);
      this.textareaComponent.isTypingPrompt = false;
    } catch (e: unknown) {
      console.error('Error sending message:', e);
      // Set specific error message for authentication issues
      if (e instanceof Error && e.message.toLowerCase().includes('auth')) {
        this.toastService.showError('Please sign in to continue');
        this.textareaComponent.isTypingPrompt = false;
      } else {
        // Generic error message for other errors
        this.toastService.showError(e instanceof Error ? e.message : 'An error occurred while sending your message');
        this.textareaComponent.isTypingPrompt = false;
      }
    }
  }

  autoResize(event: Event): void {
    const textarea = event.target as HTMLTextAreaElement;
    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = 'auto';

    // Get the line height (default is usually around 20px)
    const lineHeight = parseInt(getComputedStyle(textarea).lineHeight) || 20;
    const minHeight = lineHeight * 2; // Height for 2 rows

    // Calculate new height based on scrollHeight
    const newHeight = Math.min(Math.max(textarea.scrollHeight, minHeight), 100);
    // Apply the new height
    textarea.style.height = `${newHeight}px`;
    // Force a reflow to ensure the height is updated
    textarea.offsetHeight;
  }

  repoSelectionError(): void {
    this.toastService.showError('Please select a Study before sending your message');
    this.textareaComponent.isTypingPrompt = false;
  }
}
